import { useIntl } from "react-intl";
import type { ConnectorPropertiesSchema } from "shared-ui";
import type { FundingApplicationSchemas } from "api-types";

import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { isMarketingConnectorSelected, isRevenueConnectorSelected } from "../../../utils/connectors";
import { MESSAGES as SELECT_PLATFORMS_MESSAGES } from "../../select-platforms/message-definitions";
import { ACCOUNTING_CONNECTOR_SLUGS } from "../../../components/constants";
import { UploadDocumentType } from "../../../types";

export const getConnectedPlatformSlugs = (connectors: ConnectorPropertiesSchema[]): string[] => {
  return connectors.filter((connector) => connector.connections.length > 0).map((connector) => connector.slug);
};

export const hasAccountingConnectionsConnected = (
  connectors: ConnectorPropertiesSchema[],
  fundingApplication: FormattedFundingApplicationScreen,
  validatedDocuments?: FundingApplicationSchemas.ListDocumentsResponse[],
  isValidatedDocumentsLoading?: boolean
): boolean => {
  const connectedSlugs = getConnectedPlatformSlugs(connectors);
  const hasAccountingConnection = connectedSlugs.some(
    (slug) => ACCOUNTING_CONNECTOR_SLUGS.findIndex((connector) => connector === slug) >= 0
  );

  // Both P&L and Balance Sheet must be uploaded, with accountingDocumentsRequired confirmed and a currency selected.
  // If validatedDocuments is still loading, defer the document check to avoid a false validation error.
  const hasAccountingDocuments =
    !!fundingApplication.accountingDocumentsRequired &&
    !!fundingApplication.accountingCurrency &&
    (isValidatedDocumentsLoading === true ||
      (validatedDocuments !== undefined &&
        validatedDocuments.some((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS && d.data.length > 0) &&
        validatedDocuments.some((d) => d.documentType === UploadDocumentType.BALANCE_SHEET && d.data.length > 0)));

  const hasUnsupportedAccounting = Boolean(fundingApplication.selectedUnsupportedAccounting.length);

  return hasAccountingConnection || hasAccountingDocuments || hasUnsupportedAccounting;
};

export const validateConnectionsWithMessages = (
  connectors: ConnectorPropertiesSchema[],
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen,
  intl: ReturnType<typeof useIntl>,
  isEcommerceUnsupported = false,
  validatedDocuments?: FundingApplicationSchemas.ListDocumentsResponse[],
  isValidatedDocumentsLoading?: boolean
): string[] => {
  const validationErrors = [];
  const connectedSlugs = getConnectedPlatformSlugs(connectors);

  const isRevenueMissing =
    !!fundingApplicationConfig.revenue?.platformRequired &&
    fundingApplicationConfig.revenue?.requiredConnectorSlugs?.length === 0 &&
    !isRevenueConnectorSelected(connectedSlugs) &&
    !isEcommerceUnsupported;

  const isMarketingMissing =
    !!fundingApplicationConfig.marketing?.platformRequired &&
    fundingApplicationConfig.marketing?.requiredConnectorSlugs?.length === 0 &&
    !isMarketingConnectorSelected(connectedSlugs);

  const isAccountingMissing =
    !!fundingApplicationConfig.accounting?.platformRequired &&
    fundingApplicationConfig.accounting?.requiredConnectorSlugs?.length === 0 &&
    !hasAccountingConnectionsConnected(connectors, fundingApplication, validatedDocuments, isValidatedDocumentsLoading);

  if (isRevenueMissing && isMarketingMissing && isAccountingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingRevenueMarketingAndAccounting));
  } else if (isRevenueMissing && isAccountingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingRevenueAndAccounting));
  } else if (isRevenueMissing && isMarketingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingRevenueAndMarketing));
  } else if (isMarketingMissing && isAccountingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingMarketingAndAccounting));
  } else if (isRevenueMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingRevenuePlatform));
  } else if (isMarketingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingMarketingPlatform));
  } else if (isAccountingMissing) {
    validationErrors.push(intl.formatMessage(SELECT_PLATFORMS_MESSAGES.missingAccountingData));
  }

  return validationErrors;
};
